import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, Sparkles, Loader2 } from "lucide-react";
import api from "../lib/api";

const LEVEL_STYLES = {
  urgent: "border-[#B54D40]/40 bg-[#FCE8E4]/50",
  attention: "border-[#C07A60]/40 bg-[#FFF4E5]/50",
};

export default function SentimentAlertsBanner({ enabled, onLockClick }) {
  const [alerts, setAlerts] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!enabled) return;
    // Cheap check on mount (with low priority — don't block UI)
    let cancelled = false;
    (async () => {
      try {
        const cached = sessionStorage.getItem("nexari_sentiment_alerts");
        if (cached) {
          const parsed = JSON.parse(cached);
          if (!cancelled) setAlerts(parsed);
          // Don't refetch within 10 min
          if (Date.now() - (parsed.cachedAt || 0) < 10 * 60 * 1000) return;
        }
      } catch (e) { /* noop */ }
    })();
    return () => { cancelled = true; };
  }, [enabled]);

  async function scan() {
    setLoading(true);
    try {
      const r = await api.get("/messages/ai/sentiment-alerts");
      const payload = { ...r.data, cachedAt: Date.now() };
      setAlerts(payload);
      try { sessionStorage.setItem("nexari_sentiment_alerts", JSON.stringify(payload)); } catch (e) {/* noop */}
    } catch (e) {
      setAlerts({ alerts: [], error: true });
    } finally {
      setLoading(false);
    }
  }

  // Locked state — show inline upsell
  if (!enabled) {
    return (
      <div data-testid="sentiment-alerts-locked" className="bg-white border border-dashed border-[#C07A60]/40 rounded-xl p-4 flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2 min-w-0">
          <Sparkles className="w-4 h-4 text-[#C07A60] flex-shrink-0" strokeWidth={1.5} />
          <div className="min-w-0">
            <h3 className="font-heading font-semibold text-sm text-[#1C211F]">AI sentiment radar</h3>
            <p className="text-xs text-[#57605A] truncate">Surfaces frustrated families before they leave — included with AI Messaging Assistant.</p>
          </div>
        </div>
        <button
          data-testid="sentiment-unlock-btn"
          onClick={onLockClick}
          className="text-xs font-medium bg-[#2E5C50] text-white hover:bg-[#22463D] px-3 py-1.5 rounded-md whitespace-nowrap"
        >
          Unlock for $49/mo
        </button>
      </div>
    );
  }

  const hasFlagged = (alerts?.alerts || []).length > 0;

  return (
    <div data-testid="sentiment-alerts" className="bg-white border border-[#E8E6E1] rounded-xl p-4">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <Sparkles className="w-4 h-4 text-[#2E5C50]" strokeWidth={1.5} />
          <h3 className="font-heading font-semibold text-sm text-[#1C211F]">AI sentiment radar</h3>
        </div>
        <button
          data-testid="sentiment-scan-btn"
          onClick={scan}
          disabled={loading}
          className="text-xs font-medium text-[#2E5C50] hover:bg-[#D6E0D9]/40 px-3 py-1.5 rounded-md transition-colors disabled:opacity-60"
        >
          {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin inline" /> : "Scan now"}
        </button>
      </div>

      {alerts === null && !loading && (
        <p className="mt-2 text-xs text-[#57605A]">
          Click <strong>Scan now</strong> to surface family threads that may need attention.
        </p>
      )}

      {alerts && !loading && !hasFlagged && (
        <p data-testid="sentiment-empty" className="mt-2 text-xs text-[#57605A]">
          ✓ All families look calm. Scanned {alerts.scanned_threads || 0} threads.
        </p>
      )}

      {hasFlagged && (
        <div className="mt-3 space-y-2">
          {alerts.alerts.map((a) => (
            <Link
              key={a.family_id}
              data-testid={`sentiment-alert-${a.family_id}`}
              to="/messages"
              className={`block border rounded-md p-3 hover:opacity-90 transition-opacity ${LEVEL_STYLES[a.level] || LEVEL_STYLES.attention}`}
            >
              <div className="flex items-center gap-2 flex-wrap">
                <AlertTriangle className={`w-3.5 h-3.5 ${a.level === "urgent" ? "text-[#B54D40]" : "text-[#C07A60]"}`} strokeWidth={2} />
                <span className="font-semibold text-sm text-[#1C211F]">{a.family_name}</span>
                <span className="text-[10px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded bg-white/60 text-[#1C211F]">
                  {a.level}
                </span>
                {a.topic && <span className="text-xs text-[#57605A]">· {a.topic}</span>}
              </div>
              {a.reason && <p className="mt-1 text-xs text-[#57605A]">{a.reason}</p>}
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
